/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { useContext, useEffect, useState } from "react";
import { LoginContext } from "../../../../context/ContextProvider";
import { Button, Label } from "reactstrap";
import { getId } from "../../../../provisioning/elements/FormInput";
import ModalTextEditor from "../../../../elements/ModalTextEditor";
import CenterModal from "../../../../elements/CenterModal";
import ThemeSwitch from "../../../../elements/ThemeSwitch";
import LayoutColorForm from "../../../layouts/layoutEditForm/LayoutColorForm";
import { contrastingForegroundColor } from "../../../../util/Color";

export const defaultTermsOfService = `<h2 style="text-align:center;">Terms of Service</h2>
<p>By accessing or using Smart WiFi, you agree to comply with and be bound by the following terms and conditions of use.</p>
<ol>
  <li><strong>Use of the App</strong>: You agree to use the app for lawful purposes only.</li>
  <li><strong>Privacy Policy</strong>: Our Privacy Policy governs the collection and use of your personal information.</li>
  <li><strong>Termination</strong>: We may terminate or suspend your account and access to the app at our discretion.</li>
  <li><strong>Disclaimer</strong>: The app is provided "as is" without any warranties, expressed or implied.</li>
</ol>
<p>If you do not agree to these terms, please do not use our application.</p>
`;

export const defaultTosTextColor = "#000000";
export const defaultTosButtonBackgroundColor = "#05568E";
export const defaultTosButtonTextColor = "#FFFFFF";

const syncTosColorsToLoginContext = (loginContext, textColor, buttonBackgroundColor, buttonTextColor) => {
  loginContext.updateState({
    tosTextColor: textColor ?? defaultTosTextColor,
    tosButtonBackgroundColor: buttonBackgroundColor ?? defaultTosButtonBackgroundColor,
    tosButtonTextColor: buttonTextColor ?? defaultTosButtonTextColor,
  });
};

const TermsAndConditionsSubstep = (props) => {
  const loginContext = useContext(LoginContext);
  const preloginPreview = loginContext.preloginPreview;

  const fullPath = getId(props);
  const customKey = `${fullPath}.custom`;
  const textColorKey = `${fullPath}.textColor`;
  const buttonBackgroundColorKey = `${fullPath}.buttonBackgroundColor`;
  const buttonTextColorKey = `${fullPath}.buttonTextColor`;

  const [defaultModalOpen, setDefaultModalOpen] = useState(false);
  const [customModalOpen, setCustomModalOpen] = useState(false);

  const { watch, setValue, advanced = false } = props;

  useEffect(() => {
    const terms = watch(fullPath) ?? {};
    if (terms.textColor == null) {
      setValue(textColorKey, defaultTosTextColor);
    }
    if (terms.buttonBackgroundColor == null) {
      setValue(buttonBackgroundColorKey, defaultTosButtonBackgroundColor);
    }
    const background = terms.buttonBackgroundColor ?? defaultTosButtonBackgroundColor;
    syncTosColorsToLoginContext(
      loginContext,
      terms.textColor ?? defaultTosTextColor,
      background,
      terms.buttonTextColor ?? contrastingForegroundColor(background, 0.6)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const customHTML = watch(customKey);
  const textColor = watch(textColorKey) ?? defaultTosTextColor;
  const buttonBackgroundColor = watch(buttonBackgroundColorKey) ?? defaultTosButtonBackgroundColor;
  const storedButtonTextColor = watch(buttonTextColorKey);
  const buttonTextColor = storedButtonTextColor ?? contrastingForegroundColor(buttonBackgroundColor, 0.6);
  const isCustom = customHTML != null;

  useEffect(() => {
    syncTosColorsToLoginContext(loginContext, textColor, buttonBackgroundColor, buttonTextColor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textColor, buttonBackgroundColor, buttonTextColor]);

  const handleDefault = () => {
    setValue(customKey, null);
    setDefaultModalOpen(true);
  };

  const handleCustom = () => {
    setCustomModalOpen(true);
  };

  const handlePreloginPreviewEnabled = (enabled) => {
    loginContext.updateState({ preloginPreview: enabled });
  };

  const handleTosColorEditStart = () => {
    if (loginContext.preloginPreview && loginContext.loginMethodPreviewType == null && loginContext.loginType == null) {
      return;
    }
    loginContext.updateState({
      preloginPreview: true,
      loginType: null,
      loginMethodPreviewType: null,
      verificationRequired: false,
    });
  };

  const defaultModalStyle = {
    color: textColor !== defaultTosTextColor ? textColor : undefined,
  };

  const leftColumn = (
    <>
      <div className="d-flex">
        <Button
          color="info"
          size="lg"
          outline={isCustom}
          onClick={handleDefault}
        >
          Default
        </Button>
        <Button
          className="ms-2"
          color="info"
          size="lg"
          outline={!isCustom}
          onClick={handleCustom}
        >
          Custom
        </Button>
      </div>
      {advanced && (
        <>
          <Label className="pt-3 mb-1">Text Color</Label>
          <LayoutColorForm
            config={textColor}
            onEditStart={handleTosColorEditStart}
            onContentChange={(color) => setValue(textColorKey, color)}
          />
        </>
      )}
      <ThemeSwitch
        className="my-1"
        checked={preloginPreview}
        onChange={(checked) => handlePreloginPreviewEnabled(checked)}
        text="Preview"
      />
    </>
  );

  return (
    <div>
      {advanced ? (
        <div className="d-flex">
          <div className="flex-50 pe-3">{leftColumn}</div>
          <div className="flex-50 ps-3 d-flex flex-column align-items-stretch">
            <Label className="fw-bold w-100 d-block">Default Button Options</Label>
            <Label className="pt-2 mb-1 w-100 d-block">Button Background</Label>
            <LayoutColorForm
              config={buttonBackgroundColor}
              onEditStart={handleTosColorEditStart}
              onContentChange={(color) => setValue(buttonBackgroundColorKey, color)}
            />
            <Label className="pt-2 mb-1 w-100 d-block">Button Text</Label>
            <LayoutColorForm
              config={buttonTextColor}
              onEditStart={handleTosColorEditStart}
              onContentChange={(color) => setValue(buttonTextColorKey, color)}
            />
          </div>
        </div>
      ) : (
        leftColumn
      )}
      <CenterModal isOpen={defaultModalOpen} toggle={() => setDefaultModalOpen(prev => !prev)} size="lg">
        <div style={defaultModalStyle} dangerouslySetInnerHTML={{ __html: defaultTermsOfService }} />
      </CenterModal>
      <ModalTextEditor
        isOpen={customModalOpen}
        toggle={() => {}}
        initialHTML={isCustom ? customHTML : ""}
        forceSetHTML={defaultTermsOfService}
        onUpdate={(html) => setValue(customKey, html)}
        onDone={() => setCustomModalOpen(false)}
      />
    </div>
  );
};

export default TermsAndConditionsSubstep;
